import { useEffect, useRef, useState } from 'react';

export function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function usePrefersReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReduced(mediaQuery.matches);

    function handleChange(event: MediaQueryListEvent) {
      setPrefersReduced(event.matches);
    }

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReduced;
}

interface AnimatedPriceProps {
  from: number;
  to: number;
  duration?: number;
  delay?: number;
  formatValue: (n: number) => string;
  onComplete?: () => void;
}

export function AnimatedPrice({ from, to, duration = 1500, delay = 500, formatValue, onComplete }: AnimatedPriceProps) {
  const [displayValue, setDisplayValue] = useState(from);
  const rafRef = useRef<number | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      setDisplayValue(to);
      onCompleteRef.current?.();
      return;
    }

    const timeoutId = setTimeout(() => {
      const startTime = performance.now();

      function animate(currentTime: number) {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOut(progress);
        const currentValue = Math.round(from + (to - from) * easedProgress);

        setDisplayValue(currentValue);

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(animate);
        } else {
          onCompleteRef.current?.();
        }
      }

      rafRef.current = requestAnimationFrame(animate);
    }, delay);

    return () => {
      clearTimeout(timeoutId);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [from, to, duration, delay, prefersReducedMotion]);

  return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatValue(displayValue)}</span>;
}
